import React, { useEffect, useRef } from 'react';
import { useLocation } from 'wouter';
import { useAuth } from '../contexts/AuthContext';
import { TrendingUp, BarChart3, Zap, Shield, Globe, ChevronRight } from 'lucide-react';

export default function Login() {
  const { isAuthenticated, isGoogleReady, error } = useAuth();
  const [, navigate] = useLocation();
  const googleButtonRef = useRef<HTMLDivElement>(null);

  // Redirect to dashboard if already authenticated
  useEffect(() => {
    if (isAuthenticated) {
      navigate('/dashboard');
    }
  }, [isAuthenticated, navigate]);

  // Render Google Sign-In button
  useEffect(() => {
    if (googleButtonRef.current && isGoogleReady && !isAuthenticated) {
      try {
        // Clear any existing button first
        if (googleButtonRef.current) {
          googleButtonRef.current.innerHTML = '';
        }
        
        window.google.accounts.id.renderButton(
          googleButtonRef.current,
          {
            theme: 'filled_blue',
            size: 'large',
            type: 'standard',
            text: 'signin_with',
            width: 400,
            logo_alignment: 'left',
          }
        );
        console.log('Google button rendered successfully');
      } catch (error) {
        console.error('Error rendering Google button:', error);
      }
    }
  }, [isGoogleReady, isAuthenticated]);

  if (isAuthenticated) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-white mx-auto"></div>
          <p className="mt-4 text-white/80 text-lg">Launching your dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 relative overflow-hidden">
      {/* Animated background elements */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute -top-40 -right-40 w-80 h-80 bg-purple-500 rounded-full mix-blend-multiply filter blur-xl opacity-20 animate-blob"></div>
        <div className="absolute -bottom-40 -left-40 w-80 h-80 bg-blue-500 rounded-full mix-blend-multiply filter blur-xl opacity-20 animate-blob animation-delay-2000"></div>
        <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-80 h-80 bg-pink-500 rounded-full mix-blend-multiply filter blur-xl opacity-20 animate-blob animation-delay-4000"></div>
      </div>

      <div className="relative min-h-screen flex">
        {/* Left Side - Marketing Content */}
        <div className="hidden lg:flex lg:w-1/2 flex-col justify-center px-12 xl:px-20 text-white">
          <div className="max-w-xl">
            {/* Logo */}
            <div className="mb-8">
              <img 
                src="/logo.svg" 
                alt="PatternIQ" 
                className="h-12 w-auto mb-6"
              />
            </div>

            {/* Main Headline */}
            <h1 className="text-5xl xl:text-6xl font-bold mb-6 leading-tight">
              Unlock the Patterns<br />
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-purple-400">
                Behind Performance
              </span>
            </h1>

            <p className="text-xl text-white/80 mb-12 leading-relaxed">
              Real-time retail analytics powered by AI. Transform your data into actionable insights 
              and drive growth with enterprise-grade intelligence.
            </p>

            {/* Features */}
            <div className="space-y-6">
              <div className="flex items-start space-x-4">
                <div className="flex-shrink-0 w-12 h-12 bg-blue-500/20 rounded-lg flex items-center justify-center backdrop-blur-sm border border-blue-400/30">
                  <TrendingUp className="w-6 h-6 text-blue-400" />
                </div>
                <div>
                  <h3 className="font-semibold text-lg mb-1">Real-Time Analytics</h3>
                  <p className="text-white/70">Monitor performance metrics as they happen with live dashboards</p>
                </div>
              </div>

              <div className="flex items-start space-x-4">
                <div className="flex-shrink-0 w-12 h-12 bg-purple-500/20 rounded-lg flex items-center justify-center backdrop-blur-sm border border-purple-400/30">
                  <BarChart3 className="w-6 h-6 text-purple-400" />
                </div>
                <div>
                  <h3 className="font-semibold text-lg mb-1">Deep Dive Analysis</h3>
                  <p className="text-white/70">Comprehensive insights into revenue, inventory, and SKU performance</p>
                </div>
              </div>

              <div className="flex items-start space-x-4">
                <div className="flex-shrink-0 w-12 h-12 bg-pink-500/20 rounded-lg flex items-center justify-center backdrop-blur-sm border border-pink-400/30">
                  <Zap className="w-6 h-6 text-pink-400" />
                </div>
                <div>
                  <h3 className="font-semibold text-lg mb-1">AI-Powered Insights</h3>
                  <p className="text-white/70">Smart recommendations to optimize pricing, margins, and inventory</p>
                </div>
              </div>
            </div>

            {/* Trust Badges */}
            <div className="mt-12 pt-8 border-t border-white/10">
              <div className="flex items-center space-x-8">
                <div className="flex items-center space-x-2">
                  <Shield className="w-5 h-5 text-green-400" />
                  <span className="text-sm text-white/60">Enterprise Security</span>
                </div>
                <div className="flex items-center space-x-2">
                  <Globe className="w-5 h-5 text-blue-400" />
                  <span className="text-sm text-white/60">Cloud-Based</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Right Side - Login Form */}
        <div className="w-full lg:w-1/2 flex items-center justify-center p-8">
          <div className="w-full max-w-md">
            {/* Mobile Logo */}
            <div className="lg:hidden text-center mb-8">
              <img 
                src="/logo.svg" 
                alt="PatternIQ" 
                className="h-10 w-auto mx-auto mb-4"
              />
              <h1 className="text-3xl font-bold text-white mb-2">PatternIQ</h1>
              <p className="text-white/70">Retail Analytics Platform</p>
            </div>

            {/* Login Card */}
            <div className="bg-white/95 backdrop-blur-xl rounded-2xl shadow-2xl p-8 lg:p-10 border border-white/20">
              <div className="text-center mb-8">
                <h2 className="text-3xl font-bold text-gray-900 mb-2">
                  Welcome Back
                </h2>
                <p className="text-gray-600">
                  Sign in to access your analytics dashboard
                </p>
              </div>

              {/* Error Message */}
              {error && (
                <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-xl">
                  <div className="flex items-start">
                    <svg
                      className="w-5 h-5 text-red-500 mr-3 mt-0.5 flex-shrink-0"
                      fill="currentColor"
                      viewBox="0 0 20 20"
                    >
                      <path
                        fillRule="evenodd"
                        d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z"
                        clipRule="evenodd"
                      />
                    </svg>
                    <p className="text-sm text-red-700 leading-relaxed">{error}</p>
                  </div>
                </div>
              )}

              {/* Google Sign-In Button */}
              <div className="mb-6">
                <div 
                  ref={googleButtonRef}
                  className="w-full flex items-center justify-center"
                ></div>
              </div>

              {/* Access Info */}
              <div className="pt-6 border-t border-gray-200">
                <div className="bg-blue-50 rounded-xl p-4">
                  <div className="flex items-start">
                    <Shield className="w-5 h-5 text-blue-600 mr-3 mt-0.5 flex-shrink-0" />
                    <div>
                      <p className="text-sm font-medium text-gray-900 mb-1">
                        Secure Access
                      </p>
                      <p className="text-xs text-gray-600">
                        Access is restricted to <span className="font-semibold text-gray-900">@patterniqai.com</span> email addresses. 
                        Your data is protected with enterprise-grade security.
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Feature Highlights for Mobile */}
              <div className="lg:hidden mt-8 pt-6 border-t border-gray-200">
                <div className="space-y-3">
                  <div className="flex items-center text-sm text-gray-600">
                    <ChevronRight className="w-4 h-4 text-blue-500 mr-2" />
                    <span>Real-time analytics & insights</span>
                  </div>
                  <div className="flex items-center text-sm text-gray-600">
                    <ChevronRight className="w-4 h-4 text-purple-500 mr-2" />
                    <span>AI-powered recommendations</span>
                  </div>
                  <div className="flex items-center text-sm text-gray-600">
                    <ChevronRight className="w-4 h-4 text-pink-500 mr-2" />
                    <span>Enterprise security & compliance</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Footer */}
            <div className="text-center mt-8">
              <p className="text-sm text-white/50">
                PatternIQ v1.0.0 © 2025 | Enterprise Retail Analytics
              </p>
            </div>
          </div>
        </div>
      </div>

      <style>{`
        @keyframes blob {
          0% { transform: translate(0px, 0px) scale(1); }
          33% { transform: translate(30px, -50px) scale(1.1); }
          66% { transform: translate(-20px, 20px) scale(0.9); }
          100% { transform: translate(0px, 0px) scale(1); }
        }
        .animate-blob {
          animation: blob 7s infinite;
        }
        .animation-delay-2000 {
          animation-delay: 2s;
        }
        .animation-delay-4000 {
          animation-delay: 4s;
        }
      `}</style>
    </div>
  );
}
